/**
 * Leads page, chat style: the conversations a person has had with Ask AI, kept in this browser, and the follow-up question
 * that carries what was already asked.
 *
 * LeadPro's Ask answers one question at a time and remembers nothing, so a follow-up ("only the ones with an email") is sent
 * together with what the conversation already established. The results themselves are not stored here: every search is
 * saved on the server as a job, and a turn only keeps the job's id.
 */

export interface ChatTurn {
  id: string;
  /** What the person typed. */
  question: string;
  at: number;
  /** The server's search job whose results this turn shows. Empty when the turn failed. */
  jobId: string;
  /** How LeadPro read the question, in its own words. */
  explanation: string | null;
  truncated: boolean;
  /** Leads saved by this search, once known. */
  count: number | null;
  /** Why the search failed, when it did. */
  error: string | null;
}

export interface Chat {
  id: string;
  title: string;
  updatedAt: number;
  turns: ChatTurn[];
}

export const CHAT_LIMITS = { chats: 20, turnsPerChat: 40, title: 60, question: 1000 } as const;
const STORAGE_KEY = "voicepro:leads-chats:v1";

export function newId(): string {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

export function titleFor(question: string): string {
  const t = question.replace(/\s+/g, " ").trim();
  return t.length > CHAT_LIMITS.title ? `${t.slice(0, CHAT_LIMITS.title - 1)}…` : t;
}

const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, Math.max(0, n - 1))}…` : s);

/**
 * The text to send for `next`, given the turns already in this conversation. With nothing before it, it is `next` as typed.
 * Otherwise: what was asked before (newest last, oldest dropped first when space runs out), how the latest was understood,
 * and the new request. Never longer than `max`; if the new request alone fills it, it goes alone.
 */
export function composeQuestion(prior: ChatTurn[], next: string, max: number = CHAT_LIMITS.question): string {
  const asked = prior.filter((t) => !t.error && t.question.trim());
  const request = next.trim();
  if (asked.length === 0) return request;

  const last = asked[asked.length - 1];
  const head = "Earlier in this conversation I asked: ";
  const understood = last.explanation ? ` (you read the latest as: ${clip(last.explanation.replace(/\s+/g, " ").trim(), 200)})` : "";
  const tail = `. Now, keeping those criteria unless I change them: ${request}`;

  let budget = max - head.length - understood.length - tail.length;
  if (budget < 30) return request.slice(0, max);

  // Newest first until the budget is spent, then put them back in the order they were asked.
  const kept: string[] = [];
  // Each question costs its text plus two quotes and the ", then " that joins it to the next (9 characters).
  for (let i = asked.length - 1; i >= 0; i--) {
    const room = budget - 9;
    if (room < 20) break;
    const q = clip(asked[i].question.replace(/\s+/g, " ").trim(), Math.min(300, room));
    kept.unshift(`"${q}"`);
    budget -= q.length + 9;
  }
  return `${head}${kept.join(", then ")}${understood}${tail}`.slice(0, max);
}

type Store = Pick<Storage, "getItem" | "setItem">;

function browserStore(): Store | null {
  try { return typeof window !== "undefined" ? window.localStorage : null; } catch { return null; }
}

/** The saved conversations, newest first. Anything unreadable is dropped rather than shown half-broken. */
export function loadChats(store: Store | null = browserStore()): Chat[] {
  try {
    const raw = store?.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const chats: Chat[] = [];
    for (const c of parsed) {
      if (!c || typeof c !== "object" || typeof (c as Chat).id !== "string" || !Array.isArray((c as Chat).turns)) continue;
      const turns = (c as Chat).turns.filter((t) => t && typeof t.id === "string" && typeof t.question === "string");
      chats.push({ id: (c as Chat).id, title: String((c as Chat).title || "").slice(0, CHAT_LIMITS.title) || "New chat", updatedAt: Number((c as Chat).updatedAt) || 0, turns });
    }
    return chats.sort((a, b) => b.updatedAt - a.updatedAt).slice(0, CHAT_LIMITS.chats);
  } catch {
    return [];
  }
}

export function saveChats(chats: Chat[], store: Store | null = browserStore()): void {
  try {
    const trimmed = chats.slice(0, CHAT_LIMITS.chats).map((c) => ({ ...c, turns: c.turns.slice(-CHAT_LIMITS.turnsPerChat) }));
    store?.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch { /* storage full or blocked: the conversation still works, it just is not kept */ }
}
